import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FiBell as Bell,
  FiCheck as Check,
  FiCheckCircle as CheckCircle,
  FiCircle as Circle,
  FiTrash2 as Trash2,
  FiRefreshCw as RefreshCw,
  FiFilter as Filter,
  FiChevronLeft as ChevronLeft,
  FiChevronRight as ChevronRight,
  FiAlertTriangle as AlertTriangle,
  FiAlertCircle as AlertCircle,
  FiInfo as Info,
  FiCheckSquare as CheckSquare,
  FiSquare as Square,
  FiEye as Eye,
  FiEyeOff as EyeOff,
  FiExternalLink as ExternalLink,
  FiCloud as Cloud,
  FiCpu as Cpu,
  FiDatabase as Database,
  FiFolder as Folder,
  FiDollarSign as DollarSign,
  FiShield as Shield,
} from 'react-icons/fi';
import { IconType } from 'react-icons';
import { notificationAPI } from '../../../services/realApi';
import { useNotifications } from '../../../contexts/NotificationContext';

interface CategoryConfig {
  name: string;
  icon: IconType;
  color: string;
}

interface SeverityConfig {
  icon: IconType;
  color: string;
  label: string;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  category?: string;
  severity?: string;
  created_at?: string;
  source_service?: string;
  action_url?: string;
  action_label?: string;
}

const mapNotificationUrl = (url: string | undefined): string | undefined => {
  if (!url) return url;
  if (url.startsWith('/platformops/monitoring')) {
    return '/cloudops/endpoints';
  }
  return url;
};

const CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  platformops: { name: 'CloudOps', icon: Cloud, color: 'blue' },
  genaiops: { name: 'GenAIOps', icon: Cpu, color: 'purple' },
  dataops: { name: 'DataOps', icon: Database, color: 'cyan' },
  agentops: { name: 'AgentOps', icon: Cpu, color: 'orange' },
  project: { name: 'Projects', icon: Folder, color: 'green' },
  billing: { name: 'Billing', icon: DollarSign, color: 'amber' },
  funding: { name: 'Funding', icon: DollarSign, color: 'emerald' },
  security: { name: 'Security', icon: Shield, color: 'red' },
  general: { name: 'General', icon: Bell, color: 'gray' },
};

const SEVERITY_CONFIG: Record<string, SeverityConfig> = {
  critical: { icon: AlertCircle, color: 'red', label: 'Critical' },
  warning: { icon: AlertTriangle, color: 'amber', label: 'Warning' },
  info: { icon: Info, color: 'blue', label: 'Info' },
  success: { icon: CheckCircle, color: 'green', label: 'Success' },
};

const formatRelativeDate = (timestamp: string | undefined): string => {
  if (!timestamp) return 'Unknown';

  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleDateString();
};

const formatFullDate = (timestamp: string | undefined): string => {
  if (!timestamp) return 'Unknown';
  return new Date(timestamp).toLocaleString();
};

const NotificationsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchUnreadCount: refreshUnreadCount, unreadCount: contextUnreadCount } = useNotifications();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const [filter, setFilter] = useState(searchParams.get('filter') ?? 'all');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') ?? 'all');

  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters: Record<string, unknown> = {
        limit,
        offset: (page - 1) * limit,
      };

      if (filter === 'unread') {
        filters.unreadOnly = true;
      }

      if (categoryFilter !== 'all') {
        filters.category = categoryFilter;
      }

      const result = await notificationAPI.getNotifications(filters) as {
        success: boolean;
        data?: Notification[];
        total?: number;
        unreadCount?: number;
        error?: string;
      };

      if (result.success) {
        let data = result.data ?? [];

        if (filter === 'read') {
          data = data.filter((n) => n.is_read);
        }

        setNotifications(data);
        setTotal(result.total ?? 0);
        setUnreadCount(result.unreadCount ?? 0);
      } else {
        setError(result.error ?? 'Failed to load notifications');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load notifications';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filter, categoryFilter]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (contextUnreadCount > 0) {
      void loadNotifications();
    }
  }, [contextUnreadCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('filter', filter);
    if (categoryFilter !== 'all') params.set('category', categoryFilter);
    setSearchParams(params, { replace: true });
  }, [filter, categoryFilter, setSearchParams]);

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map((n) => n.id)));
    }
    setSelectAll(!selectAll);
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
    setSelectAll(newSelected.size === notifications.length);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    setActionLoading(true);
    try {
      const result = await notificationAPI.markAsRead(notificationId) as { success: boolean };
      if (result.success) {
        setNotifications((prev) => prev.map((n) => n.id === notificationId ? { ...n, is_read: true } : n));
        setUnreadCount((prev) => Math.max(0, prev - 1));
        refreshUnreadCount();
      }
    } catch (err: unknown) {
      console.error('Error marking as read:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAsUnread = async (notificationId: string) => {
    setActionLoading(true);
    try {
      const result = await notificationAPI.markAsUnread(notificationId) as { success: boolean };
      if (result.success) {
        setNotifications((prev) => prev.map((n) => n.id === notificationId ? { ...n, is_read: false } : n));
        setUnreadCount((prev) => prev + 1);
        refreshUnreadCount();
      }
    } catch (err: unknown) {
      console.error('Error marking as unread:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (notificationId: string) => {
    setActionLoading(true);
    try {
      const result = await notificationAPI.deleteNotification(notificationId) as { success: boolean };
      if (result.success) {
        const notification = notifications.find((n) => n.id === notificationId);
        if (notification && !notification.is_read) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
          refreshUnreadCount();
        }
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        setTotal((prev) => prev - 1);
        const newSelected = new Set(selectedIds);
        newSelected.delete(notificationId);
        setSelectedIds(newSelected);
      }
    } catch (err: unknown) {
      console.error('Error deleting notification:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkMarkAsRead = async () => {
    if (selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      const result = await notificationAPI.bulkMarkAsRead(Array.from(selectedIds)) as { success: boolean };
      if (result.success) {
        const unreadSelected = notifications.filter((n) => selectedIds.has(n.id) && !n.is_read).length;
        setNotifications((prev) => prev.map((n) => selectedIds.has(n.id) ? { ...n, is_read: true } : n));
        setUnreadCount((prev) => Math.max(0, prev - unreadSelected));
        setSelectedIds(new Set());
        setSelectAll(false);
        refreshUnreadCount();
      }
    } catch (err: unknown) {
      console.error('Error bulk marking as read:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkMarkAsUnread = async () => {
    if (selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      const result = await notificationAPI.bulkMarkAsUnread(Array.from(selectedIds)) as { success: boolean };
      if (result.success) {
        const readSelected = notifications.filter((n) => selectedIds.has(n.id) && n.is_read).length;
        setNotifications((prev) => prev.map((n) => selectedIds.has(n.id) ? { ...n, is_read: false } : n));
        setUnreadCount((prev) => prev + readSelected);
        setSelectedIds(new Set());
        setSelectAll(false);
        refreshUnreadCount();
      }
    } catch (err: unknown) {
      console.error('Error bulk marking as unread:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!globalThis.confirm(`Delete ${selectedIds.size} notification(s)?`)) return;

    setActionLoading(true);
    try {
      const result = await notificationAPI.bulkDelete(Array.from(selectedIds)) as { success: boolean };
      if (result.success) {
        const unreadDeleted = notifications.filter((n) => selectedIds.has(n.id) && !n.is_read).length;
        setNotifications((prev) => prev.filter((n) => !selectedIds.has(n.id)));
        setTotal((prev) => prev - selectedIds.size);
        setUnreadCount((prev) => Math.max(0, prev - unreadDeleted));
        setSelectedIds(new Set());
        setSelectAll(false);
        refreshUnreadCount();
      }
    } catch (err: unknown) {
      console.error('Error bulk deleting:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    setActionLoading(true);
    try {
      const result = await notificationAPI.markAllAsRead() as { success: boolean };
      if (result.success) {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        setUnreadCount(0);
        refreshUnreadCount();
      }
    } catch (err: unknown) {
      console.error('Error marking all as read:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const getCategoryInfo = (notification: Notification): CategoryConfig => {
    const category = notification.category ?? 'general';
    return CATEGORY_CONFIG[category] ?? CATEGORY_CONFIG.general;
  };

  const getSeverityInfo = (notification: Notification): SeverityConfig => {
    const severity = notification.severity ?? 'info';
    return SEVERITY_CONFIG[severity] ?? SEVERITY_CONFIG.info;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Notifications</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {unreadCount} unread of {total} total
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => void loadNotifications()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {unreadCount > 0 && (
            <button
              onClick={() => void handleMarkAllAsRead()}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <Check className="w-4 h-4" />
              Mark All Read
            </button>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setFilter(e.target.value); setPage(1); }}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Notifications</option>
                <option value="unread">Unread Only</option>
                <option value="read">Read Only</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={categoryFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setCategoryFilter(e.target.value); setPage(1); }}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Categories</option>
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.name}</option>
                ))}
              </select>
            </div>

            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedIds.size} selected
                </span>
                <button
                  onClick={() => void handleBulkMarkAsRead()}
                  disabled={actionLoading}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  title="Mark selected as read"
                >
                  <Eye className="w-4 h-4" />
                  Read
                </button>
                <button
                  onClick={() => void handleBulkMarkAsUnread()}
                  disabled={actionLoading}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  title="Mark selected as unread"
                >
                  <EyeOff className="w-4 h-4" />
                  Unread
                </button>
                <button
                  onClick={() => void handleBulkDelete()}
                  disabled={actionLoading}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50"
                  title="Delete selected"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="mx-4 my-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <p className="text-red-800 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        <div className="overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleSelectAll}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {selectAll ? (
                  <CheckSquare className="w-5 h-5 text-blue-600" />
                ) : (
                  <Square className="w-5 h-5" />
                )}
              </button>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {notifications.length} notification{notifications.length === 1 ? '' : 's'}
              </span>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!loading && notifications.length === 0 && (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                No notifications
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {filter === 'unread' ? "You're all caught up!" : 'No notifications to display.'}
              </p>
            </div>
          )}

          {!loading && notifications.length > 0 && (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {notifications.map((notification) => {
                const categoryInfo = getCategoryInfo(notification);
                const severityInfo = getSeverityInfo(notification);
                const CategoryIcon = categoryInfo.icon;
                const SeverityIcon = severityInfo.icon;
                const isSelected = selectedIds.has(notification.id);

                let rowClass = 'px-4 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors';
                if (isSelected) {
                  rowClass += ' bg-blue-100/50 dark:bg-blue-900/20';
                } else if (!notification.is_read) {
                  rowClass += ' bg-blue-50/50 dark:bg-blue-900/10';
                }

                return (
                  <div key={notification.id} className={rowClass}>
                    <div className="flex items-start gap-4">
                      <button
                        onClick={() => toggleSelect(notification.id)}
                        className="mt-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>

                      <div className="mt-2">
                        {notification.is_read ? (
                          <Circle className="w-2.5 h-2.5 text-transparent" />
                        ) : (
                          <Circle className="w-2.5 h-2.5 fill-blue-600 text-blue-600" />
                        )}
                      </div>

                      <div className={`p-2 rounded-lg bg-${categoryInfo.color}-100 dark:bg-${categoryInfo.color}-900/30`}>
                        <CategoryIcon className={`w-5 h-5 text-${categoryInfo.color}-600 dark:text-${categoryInfo.color}-400`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className={`text-sm font-medium ${notification.is_read ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'}`}>
                                {notification.title}
                              </h3>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-${severityInfo.color}-100 dark:bg-${severityInfo.color}-900/30 text-${severityInfo.color}-700 dark:text-${severityInfo.color}-400`}>
                                <SeverityIcon className="w-3 h-3" />
                                {severityInfo.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                              {notification.message}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-500">
                              <span title={formatFullDate(notification.created_at)}>
                                {formatRelativeDate(notification.created_at)}
                              </span>
                              {notification.source_service && (
                                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                                  {notification.source_service}
                                </span>
                              )}
                              {notification.category && (
                                <span className="capitalize">{notification.category}</span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            {notification.action_url && (
                              <a
                                href={mapNotificationUrl(notification.action_url)}
                                onClick={() => {
                                  if (!notification.is_read) {
                                    void notificationAPI.markAsRead(notification.id);
                                  }
                                }}
                                className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                title={notification.action_label ?? 'View'}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                            {notification.is_read ? (
                              <button
                                onClick={() => void handleMarkAsUnread(notification.id)}
                                disabled={actionLoading}
                                className="p-2 text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                                title="Mark as unread"
                              >
                                <EyeOff className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => void handleMarkAsRead(notification.id)}
                                disabled={actionLoading}
                                className="p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                                title="Mark as read"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => void handleDelete(notification.id)}
                              disabled={actionLoading}
                              className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900/50">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Page {page} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
